import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Bell,
	BellOff,
	LogOut,
	Pencil,
	Star,
	StarOff,
	Trash2,
	UserPlus,
	Users,
	Video,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
	ActionSheet,
	BottomSheet,
	BottomSheetTextButton,
	MobileList,
	MobileListRow,
	MobilePushedPage,
} from '@/components/mobile';
import AttachmentSidebarViewer from './AttachmentSidebarViewer';
import Notes from './Notes';
import TeamConversationAvatar from './TeamConversationAvatar';
import UserAvatar from './UserAvatar';
import { channelMembershipText } from './channels';
import useTeamConversationActions from './useTeamConversationActions';
import { getOnlineStatus } from './onlineStatus';

const SectionTitle = ({ children }) => (
	<h2 className="px-8 pb-1.5 pt-6 text-[13px] font-normal uppercase tracking-wide text-muted-foreground">
		{children}
	</h2>
);

const Card = ({ className, children, ...props }) => (
	<div
		className={cn(
			'mx-4 overflow-hidden rounded-xl bg-background ring-1 ring-border/70 dark:bg-zinc-900 dark:ring-white/10',
			className
		)}
		{...props}
	>
		{children}
	</div>
);

const ActionTile = ({ icon: Icon, label, onClick, disabled, pressed }) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		aria-pressed={pressed}
		className="flex min-h-16 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl bg-background px-1 py-2 text-[12px] font-medium text-blue-600 ring-1 ring-border/70 transition-colors active:bg-muted disabled:opacity-40 dark:bg-zinc-900 dark:text-blue-400 dark:ring-white/10 dark:active:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
	>
		<Icon className="size-5" aria-hidden="true" />
		<span className="max-w-full truncate">{label}</span>
	</button>
);

/**
 * Phones: a team conversation's details as one pushed screen (what the
 * desktop splits between the title dialog, the ••• side panel and the row
 * menu): who is in it, video, add people, mute, star, notes, media, rename,
 * leave and delete. The thread stays mounted underneath.
 */
const TeamInfoPage = ({
	open,
	onClose,
	// the thread's back to the list, used after leaving the group
	onMobileBack,
	conversation,
	directMessage,
	userFromDirectMessage,
	onStartVideo,
	onAddPeople,
	onRename,
	onDelete,
	isDeletingGroup,
	isAttachmentsModalOpen,
	handleIsAttachmentsModalOpen,
}) => {
	const users = useSelector((state) => state.users);
	const auth = useSelector((state) => state.auth);
	const actions = useTeamConversationActions(conversation, {
		isDirectMessage: directMessage,
		onMobileBack,
	});

	const [renameOpen, setRenameOpen] = useState(false);
	const [renameValue, setRenameValue] = useState('');
	const [confirmOpen, setConfirmOpen] = useState(false);
	// Which confirmation the sheet shows. Kept after closing, so the sheet
	// slides away with its title and button still in place.
	const [confirm, setConfirm] = useState(null);

	if (!conversation) {
		return null;
	}

	const isChannel = Boolean(conversation.channel);
	const memberIds = conversation._users || [];
	const members = memberIds
		.map((id) => users.find((user) => user._id === id))
		.filter(Boolean)
		.sort((a, b) => {
			const aOnline = a._id === auth.user._id || a.isOnline;
			const bOnline = b._id === auth.user._id || b.isOnline;
			return aOnline === bOnline ? 0 : aOnline ? -1 : 1;
		});
	const memberCount = memberIds.length;
	const title = directMessage
		? userFromDirectMessage?.name || 'Direct message'
		: conversation.title || 'Untitled';
	const subtitle = directMessage
		? getOnlineStatus(userFromDirectMessage)
		: `${isChannel ? 'Channel · ' : ''}${memberCount} ${memberCount === 1 ? 'member' : 'members'}`;
	const canVideo = directMessage ? Boolean(userFromDirectMessage?.active) : true;
	const canEditGroup = !directMessage && !isChannel;
	const pageTitle = directMessage ? 'Details' : isChannel ? 'Channel info' : 'Group info';

	const openRename = () => {
		setRenameValue(conversation.title || '');
		setRenameOpen(true);
	};

	const openConfirm = (kind) => {
		setConfirm(kind);
		setConfirmOpen(true);
	};

	const saveRename = () => {
		const next = renameValue.trim();
		if (next && next !== conversation.title) {
			onRename(next);
		}
		setRenameOpen(false);
	};

	const confirmSheet = {
		video: {
			title: 'Start a video chat?',
			description: 'This posts a link that teammates can join for a video call.',
			items: [{ label: 'Start video chat', icon: Video, onSelect: onStartVideo }],
		},
		leave: {
			title: `Leave ${title}?`,
			description: 'You will stop getting its messages. Someone in the group can add you back.',
			items: [{ label: 'Leave group', icon: LogOut, destructive: true, onSelect: actions.leave }],
		},
		delete: {
			title: `Delete ${title}?`,
			description: 'This deletes the group and its messages for everyone. It cannot be undone.',
			items: [
				{
					label: 'Delete group',
					icon: Trash2,
					destructive: true,
					disabled: isDeletingGroup,
					onSelect: onDelete,
				},
			],
		},
	}[confirm];

	return (
		<MobilePushedPage
			open={open}
			onClose={onClose}
			title={pageTitle}
			bodyClassName="bg-muted/40 pb-8 dark:bg-background"
		>
			<div className="flex flex-col items-center gap-2 px-4 pb-2 pt-6 text-center">
				<TeamConversationAvatar
					conversation={conversation}
					directMessageUser={directMessage ? userFromDirectMessage : null}
					size={72}
				/>
				<div className="min-w-0 max-w-full">
					<h1 className="line-clamp-2 break-words text-xl font-semibold leading-tight">{title}</h1>
					<p
						className={cn(
							'mt-0.5 truncate text-sm',
							directMessage && userFromDirectMessage?.isOnline
								? 'text-emerald-600 dark:text-emerald-400'
								: 'text-muted-foreground'
						)}
					>
						{subtitle}
					</p>
					{directMessage && userFromDirectMessage?.username && (
						<p className="truncate text-sm text-muted-foreground">
							{userFromDirectMessage.username}
						</p>
					)}
				</div>
			</div>

			<div className="flex gap-2 px-4 pt-3" data-onboarding="teams-details">
				{canVideo && (
					<ActionTile icon={Video} label="Video" onClick={() => openConfirm('video')} />
				)}
				{canEditGroup && (
					<ActionTile icon={UserPlus} label="Add" onClick={onAddPeople} />
				)}
				{!directMessage && isChannel && (
					<ActionTile icon={Users} label="Members" onClick={onAddPeople} />
				)}
				<ActionTile
					icon={actions.isMuted ? Bell : BellOff}
					label={actions.isMuted ? 'Unmute' : 'Mute'}
					onClick={actions.toggleMute}
				/>
				<ActionTile
					icon={actions.isStarred ? StarOff : Star}
					label={actions.isStarred ? 'Unstar' : 'Star'}
					onClick={actions.toggleStar}
				/>
			</div>

			{isChannel && (
				<p className="px-8 pt-3 text-[13px] leading-snug text-muted-foreground">
					{channelMembershipText(conversation)}
				</p>
			)}

			<section data-onboarding="teams-notes">
				<SectionTitle>Notes</SectionTitle>
				<Card className="p-3">
					{conversation.notes?.length === 0 && (
						<p className="mb-2 text-sm leading-snug text-muted-foreground">
							Notes are shared with everyone here. Add one below, or pin a message to it.
						</p>
					)}
					<Notes activeTeamConversation={conversation} key={conversation._id} />
				</Card>
			</section>

			<section>
				<SectionTitle>Media</SectionTitle>
				<Card className="p-2">
					<AttachmentSidebarViewer
						isAttachmentsModalOpen={isAttachmentsModalOpen}
						handleIsAttachmentsModalOpen={handleIsAttachmentsModalOpen}
					/>
				</Card>
			</section>

			{!directMessage && (
				<section>
					<SectionTitle>
						{memberCount} {memberCount === 1 ? 'member' : 'members'}
					</SectionTitle>
					<MobileList inset aria-label="Members">
						{canEditGroup && (
							<MobileListRow
								leading={
									<span className="flex size-9 items-center justify-center rounded-full bg-muted text-blue-600 dark:bg-zinc-800 dark:text-blue-400">
										<UserPlus className="size-5" aria-hidden="true" />
									</span>
								}
								title={<span className="text-blue-600 dark:text-blue-400">Add people</span>}
								onClick={onAddPeople}
							/>
						)}
						{members.map((member) => (
							<MobileListRow
								key={member._id}
								leading={
									<UserAvatar
										user={member}
										showName={false}
										showNameBelow={false}
										avatarWidth="36px"
										avatarHeight="36px"
										avatarFontSize="13px"
										onlineIndicatorHeight="0.6rem"
										onlineIndicatorWidth="0.6rem"
									/>
								}
								title={
									<>
										{member.name}
										{member._id === auth.user._id && (
											<span className="font-normal text-muted-foreground"> (you)</span>
										)}
									</>
								}
								subtitle={member.username}
								meta={
									member.isOnline ? (
										<span className="text-emerald-600 dark:text-emerald-400">Online</span>
									) : null
								}
							/>
						))}
					</MobileList>
				</section>
			)}

			{(canEditGroup || actions.canLeave) && (
				<MobileList inset className="mt-8" aria-label="Group actions">
					{canEditGroup && (
						<MobileListRow
							leading={<Pencil className="size-5 text-muted-foreground" aria-hidden="true" />}
							title="Rename group"
							onClick={openRename}
							chevron
						/>
					)}
					{actions.canLeave && (
						<MobileListRow
							leading={<LogOut className="size-5 text-red-600" aria-hidden="true" />}
							title={<span className="text-red-600">Leave group</span>}
							onClick={() => openConfirm('leave')}
						/>
					)}
					{canEditGroup && (
						<MobileListRow
							leading={<Trash2 className="size-5 text-red-600" aria-hidden="true" />}
							title={<span className="text-red-600">Delete group</span>}
							onClick={() => openConfirm('delete')}
							disabled={isDeletingGroup}
						/>
					)}
				</MobileList>
			)}

			<BottomSheet
				open={renameOpen}
				onOpenChange={setRenameOpen}
				title="Rename group"
				headerLeft={
					<BottomSheetTextButton onClick={() => setRenameOpen(false)}>Cancel</BottomSheetTextButton>
				}
				headerRight={
					<BottomSheetTextButton
						strong
						onClick={saveRename}
						disabled={!renameValue.trim() || renameValue.trim() === conversation.title}
					>
						Save
					</BottomSheetTextButton>
				}
			>
				<form
					className="px-4 pb-4 pt-1"
					onSubmit={(event) => {
						event.preventDefault();
						saveRename();
					}}
				>
					<Input
						aria-label="Group name"
						value={renameValue}
						onChange={(event) => setRenameValue(event.target.value)}
						enterKeyHint="done"
						autoFocus
						className="h-11"
					/>
				</form>
			</BottomSheet>

			<ActionSheet
				open={confirmOpen && Boolean(confirmSheet)}
				onOpenChange={setConfirmOpen}
				title={confirmSheet?.title || ''}
				description={confirmSheet?.description}
				items={confirmSheet?.items || []}
			/>
		</MobilePushedPage>
	);
};

export default TeamInfoPage;
